import * as React from "react"
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import {
  BookOpenCheck,
  Building2, ChevronRight,
  Layers,
  ScrollText,
  UsersRound,
  ClipboardCheck,
  Globe,
  LayoutDashboard,
  MessageSquare,
  LogOut,
  Compass,
  Rocket,
  TrendingUp,
  UserRound,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { GuiaDeLaPlataforma } from "@/components/GuiaDeLaPlataforma"
import { MartyFlotante } from "@/components/MartyFlotante"
import { MartyNota } from "@/components/MartyNota"
import { EntitySelect } from "@/components/layout/EntitySelect"
import { LineaDeMargen, Logotype } from "@/components/layout/Logotype"
import { ThemeToggle } from "@/components/layout/ThemeToggle"
import { useAuth, useRbac } from "@/lib/auth"
import { paisDeLaPantalla } from "@/lib/entities"
import { USE_MOCKS } from "@/lib/api"
import { I18nProvider, useI18n, t } from "@/lib/i18n"
import { VozProvider } from "@/lib/voz"
import { CAPABILITY_LABELS, ROLE_LABELS } from "@/lib/types"
import { puedeVer } from "@/lib/secciones"
import type { Seccion } from "@/lib/secciones"
import { cn } from "@/lib/utils"
import { BotonDeIdioma } from "@/components/layout/SelectorDeIdioma"

interface NavItem {
  to: string
  /**
   * La CLAVE de la entrada, no su texto.
   *
   * El nombre y la bajada se resuelven con `t("nav_<clave>")` y
   * `t("nav_<clave>_hint")` en el idioma que eligió la persona. Tener aquí la
   * cadena en español ataba el riel —lo que se ve en TODAS las pantallas— al
   * único idioma en que estaba escrito.
   */
  clave: string
  icon: typeof ClipboardCheck
  /**
   * Se muestra solo a los roles que tienen esta seccion. Ver `lib/secciones`.
   *
   * `null` = sin gatear. Es para las pantallas que EXPLICAN el producto en vez
   * de operar sobre el: filtrarlas por rol las escondería justo a quien acaba
   * de llegar y todavia no tiene rol asignado, que es cuando mas hacen falta.
   * Sigue la misma regla que ya rige el resto del gating: sin rol conocido no
   * se gatea, porque no saber que rol tiene alguien no es saber que no tiene
   * ninguno.
   */
  seccion: Seccion | null
}

/**
 * TRES SECCIONES, y la division no es estetica: separa MIRAR de TOCAR.
 *
 *   Dashboard    solo lectura. Analisis, sin un solo control que edite.
 *   Finanzas     el modelo de gestion, editable.
 *   Contabilidad la operacion, editable.
 *
 * Antes habia seis entradas sueltas —revision, libros, maestros, finanzas,
 * caja, consola— y cada una era un destino final. Eso obliga a saber de memoria
 * cual es cual. Con tres puertas, cada seccion elige adentro que vista mostrar.
 *
 * El menu lateral NO repite las vistas de cada seccion: eso lo hacen las
 * pestanas de su layout. Dos navegaciones para lo mismo obligan a mirar las dos
 * para saber donde se esta.
 */
const NAV_ITEMS: NavItem[] = [
  {
    to: "/primeros-pasos",
    clave: "primeros_pasos",
    icon: Rocket,
    // Sin `seccion`: es la pantalla que explica el producto, no una que opera
    // sobre el, asi que la ve cualquiera con acceso. Filtrarla por rol la
    // escondería justo a quien acaba de llegar.
    seccion: null,
  },
  {
    to: "/dashboard",
    clave: "dashboard",
    icon: LayoutDashboard,
    seccion: "dashboard",
  },
  {
    to: "/finanzas",
    clave: "finanzas",
    icon: TrendingUp,
    seccion: "finanzas",
  },
  {
    to: "/contabilidad",
    clave: "contabilidad",
    icon: BookOpenCheck,
    seccion: "contabilidad",
  },
  {
    /*
      EL GRUPO, al mismo nivel que las otras tres y no dentro de ninguna. No es
      otra forma de mirar la contabilidad de una sociedad: es otro sujeto. Y va
      al final porque se llega a él cuando las cuatro sociedades ya están
      cuadradas, no antes.
    */
    to: "/holding",
    clave: "holding",
    icon: Layers,
    seccion: "holding",
  },
  {
    /*
      La pista de auditoría. Va al final y con sus propios roles: la ven el
      auditor, el contador y el administrador de plataforma, y nadie más — ni
      siquiera quien tiene permiso de lectura sobre todo lo demás.
    */
    to: "/auditoria",
    clave: "auditoria",
    icon: ScrollText,
    seccion: "auditoria",
  },
  {
    /*
      PENDIENTES: lo que alguien marcó como que no cuadra. Sin `seccion`, y por
      la misma razon que Accesos: quien deja un comentario es el que MIRA —un
      gerente, un socio, alguien de solo lectura— y colgarlo de Contabilidad lo
      escondería justo del rol para el que se construyó.

      Va antes de Accesos porque es trabajo diario y aquello es administración.
    */
    to: "/pendientes",
    clave: "pendientes",
    icon: MessageSquare,
    seccion: null,
  },
  {
    /*
      ACCESOS. Sin `seccion`: quien administra el equipo de una empresa no tiene
      por que ser quien lleva su contabilidad, y colgarlo de una seccion lo
      esconderia justo del rol que lo necesita. Quien no tenga `manage_access`
      entra y la pantalla se lo dice; el backend lo exige en cada llamada.
    */
    to: "/accesos",
    clave: "accesos",
    icon: UsersRound,
    seccion: null,
  },
]

export function AppLayout() {
  const { activeMembership } = useRbac()
  const { entities, activeEntityId, profile } = useAuth()
  /*
    EL PAIS SIGUE A LA SOCIEDAD, NO AL CLIENTE.

    Era el pais del tenant, y era correcto mientras cada cliente tuviera una
    sola sociedad. Con cuatro —Siete opera en PE, MX, BR y PA bajo un tenant
    peruano— abrir la sociedad de Sao Paulo mostraba la pantalla en español,
    con soles y la palabra RUC, sobre datos de una empresa que declara en
    portugues, en reales y con CNPJ. La regla vive en `lib/entities.ts` para
    poder probarla sin montar React.
  */
  const pais = paisDeLaPantalla(entities, activeEntityId, activeMembership?.country)
  /*
    Y EL IDIOMA SIGUE A LA PERSONA. Son dos ejes distintos y hasta hoy estaban
    pegados: el pais decide la moneda, el formato y como se llama el impuesto;
    el idioma decide si el boton dice «Guardar», «Save» o «Salvar». Un contador
    brasileño que lleva la sociedad peruana quiere el producto en portugues sin
    que por eso el IGV pase a llamarse ICMS. Ver `lib/i18n.tsx`.

    VozProvider va DENTRO de I18nProvider y dentro del Router: necesita las dos
    cosas —el idioma de la sociedad y la ruta actual— para saber que diria Marty
    en esta pantalla. Ver `lib/voz.tsx`.
  */
  return (
    <I18nProvider country={pais} idiomaDelUsuario={profile?.idioma ?? null}>
      <VozProvider>
        <AppShell />
      </VozProvider>
    </I18nProvider>
  )
}

function AppShell() {
  const { profile, activeTenantId, setActiveTenantId, signOut } = useAuth()

  /*
    LA GUÍA NO SE ABRE SOLA AL ENTRAR. Cesar la pidió DESPUÉS de terminar el
    traspaso —«finalizar onboarding o skipearlo y ya recién ahí que te haga una
    guía»— así que la lanza esa pantalla (ver `PrimerosPasosPage`) por
    `localStorage`, y aquí sólo se lee. Abrirla en cada entrada la convertiría en
    un obstáculo; la puerta para volver a verla está en el menú de abajo.
  */
  /*
    SOBREVIVE A UN CAMBIO DE IDIOMA. `I18nProvider` vuelve a montar la app
    entera cuando cambia el idioma (`key={idioma}`), y elegir la sociedad de
    Brasil lo cambia solo: la guía se cerraba a mitad del recorrido sin que
    nadie la cerrara. Se guarda abierta en `sessionStorage` y se recupera.
  */
  const [guiaAbierta, setGuiaAbierta] = React.useState(() => {
    try { return sessionStorage.getItem("marty.guia.abierta") === "1" } catch { return false }
  })
  React.useEffect(() => {
    try {
      if (guiaAbierta) sessionStorage.setItem("marty.guia.abierta", "1")
      else sessionStorage.removeItem("marty.guia.abierta")
    } catch { /* sin sessionStorage: sólo se pierde esto */ }
  }, [guiaAbierta])
  React.useEffect(() => {
    const pedida = sessionStorage.getItem("marty.guia.lanzar") === "1"
    if (pedida) {
      sessionStorage.removeItem("marty.guia.lanzar")
      setGuiaAbierta(true)
    }
  }, [])
  const { memberships, activeMembership, capabilities, roles, tenantWide } = useRbac()
  const i18n = useI18n()
  const { t } = i18n
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  /*
    `?guia=1` LA ABRE DESDE UN ENLACE. Hasta ahora sólo se lanzaba al terminar
    Primeros pasos, así que no había forma de mandarle a alguien «mira esto» ni
    de volver a verla — el comentario de arriba prometía una puerta en el menú
    que no existía. Se quita el parámetro al abrirla: recargar la página no
    tiene que volver a lanzarla.
  */
  React.useEffect(() => {
    const q = new URLSearchParams(search)
    if (q.get("guia") === "1") {
      setGuiaAbierta(true)
      q.delete("guia")
      const resto = q.toString()
      navigate(`${pathname}${resto ? `?${resto}` : ""}`, { replace: true })
    }
  }, [search])  // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSignOut() {
    await signOut()
    navigate("/login", { replace: true })
  }

  // El menu se filtra por rol (`lib/secciones.ts`). Esto NO es control de
  // acceso —eso es el 403 del backend y RLS—: es no ponerle a un contador seis
  // pantallas de gestion que no usa.
  //
  // Estuvo desactivado mientras `membershipsFromApi` conservaba un solo rol por
  // tenant: gatear con ese dato escondia Contabilidad a alguien que si es
  // contador. Ahora la membresia lleva `roles` entera y `useRbac().roles` las
  // une todas, que es la lista que se pasa aca.
  //
  // Si la persona no tuviera ningun rol conocido el menu quedaria vacio, que no
  // se distingue de una pantalla rota: en ese caso se muestra todo y que sea el
  // backend quien responda.
  //
  // Se pasa tambien el ALCANCE, que es el segundo eje. El rol dice a que se
  // dedica alguien; el alcance, sobre que. Sin el, un contador acotado a una
  // sociedad veia «Auditoria» en el riel y al entrar recibia 403 — un enlace
  // que no lleva a ningun sitio es peor que no tenerlo.
  const permitidas = NAV_ITEMS.filter(
    (item) => item.seccion === null || puedeVer(item.seccion, roles, { tenantWide }),
  )
  const visibleNav = permitidas.length > 0 ? permitidas : NAV_ITEMS

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      {/* ---------------- Riel de navegacion ---------------- */}
      {/*
        EL RIEL, en verde libro con rayado oscuro (brandbook, lamina 20). Los
        colores y el por que de que no sigan al tema estan en la clase `.riel`
        de `index.css`, junto a los contrastes medidos.
      */}
      <aside data-guia="riel" className="riel relative flex w-rail shrink-0 flex-col border-r riel-filete">
        {/*
          LA LINEA DE MARGEN DEL RIEL, de arriba abajo.

          Brandbook lamina 09: la linea «atraviesa el soporte de arriba a abajo».
          El riel ES el soporte, asi que la linea es suya y no del logotipo — que
          es lo que la volvia un adorno de veinte pixeles en la esquina.

          Y ESTO OBLIGA A CAMBIAR COMO SE MARCA LO ACTIVO. El rojo «senala, no
          tapiza: un acento por pieza». Con la linea de arriba abajo, el acento
          rojo del riel ya esta gastado, asi que el modulo activo NO puede ser
          otro filete rojo. Pasa a marcarse en FOLIO PLENO, que es justo lo que
          manda la lamina 20 para la pestana activa. Se ve mas y compite menos.
        */}
        <LineaDeMargen left="0" grosor="2px" className="z-10" />

        <div className="riel-filete flex shrink-0 items-center border-b py-4 pl-[0.9rem] pr-3">
          {/* Sobre el verde va la version NEGATIVA: folio con el punto rojo. Y
              `sobreLinea`, porque la linea la pone el riel. */}
          <Logotype
            variante="sobreLinea"
            tamano="md"
            className="h-auto text-[hsl(var(--riel-texto))] dark:text-[hsl(var(--riel-texto))]"
          />
        </div>

        <nav className="flex-1 space-y-1.5 overflow-y-auto p-2" aria-label={t("Módulos")}>
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-guia={`menu-${item.to.replace(/^\//, "")}`}
              className={({ isActive }) =>
                cn(
                  "group relative flex items-start gap-2.5 rounded-md py-2 pl-3 pr-2 text-xs",
                  /*
                    QUE SE NOTE QUE ES UN BOTON. Brandbook lamina 16: «el salto
                    entre estados debe verse sin buscarlo».

                    El hover anterior era un fondo al 10% de folio: sobre el verde
                    libro eso es casi nada, y en un riel de siete entradas no hay
                    forma de saber que son pulsables hasta que se pulsa una. Ahora
                    el hover hace TRES cosas a la vez —fondo al 16%, borde de
                    verde foco y el texto a folio pleno— y el bloque se desplaza
                    un pixel. Tres senales porque una sola, sobre una textura
                    rayada, se pierde.
                  */
                  "border border-transparent transition-all duration-100",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive
                    ? "riel-activo shadow-xs"
                    : cn(
                        "riel-texto-apagado",
                        "hover:translate-x-px hover:border-[hsl(var(--riel-acento)/0.45)]",
                        "hover:bg-[hsl(var(--riel-texto)/0.16)] hover:text-[hsl(var(--riel-texto))]",
                        /* Presionado: baja casi a tinta, como fija la lamina 16. */
                        "active:bg-[hsl(157.8_47.4%_11.2%)]",
                      ),
                )
              }
            >
              {({ isActive }) => (
                <>
                  {/*
                    AQUI ESTABA EL LOMO ROJO del modulo activo, y se quito a
                    proposito: la linea de margen del riel ya es el acento rojo de
                    la pieza, y el brandbook admite UNO. Dos filetes rojos en la
                    misma columna —uno fijo de arriba abajo y otro que salta de
                    entrada en entrada— hacen que ninguno de los dos signifique
                    nada.

                    Lo activo se marca en folio pleno (`riel-activo`), que es lo
                    que la lamina 20 fija para la pestana activa.
                  */}
                  <item.icon
                    className={cn(
                      "mt-px h-4 w-4 shrink-0 transition-colors",
                      isActive
                        ? "text-[hsl(155.2_42.6%_21.2%)]"
                        : "riel-acento group-hover:text-[hsl(var(--riel-texto))]",
                    )}
                  />
                  <span className="min-w-0 leading-tight">
                    <span className="block font-medium">{t(`nav_${item.clave}`)}</span>
                    <span
                      className={cn(
                        "mt-px block text-2xs leading-[0.9rem]",
                        isActive ? "opacity-70" : "opacity-80",
                      )}
                    >
                      {t(`nav_${item.clave}_hint`)}
                    </span>
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="riel-filete space-y-2 border-t p-2">
          <div>
            <div className="eyebrow riel-texto-apagado mb-1">{t("Capacidades")}</div>
            <div className="flex flex-wrap gap-1">
              {capabilities.length === 0 ? (
                <span className="riel-texto-apagado text-2xs">{t("Sin capacidades asignadas")}</span>
              ) : (
                capabilities.map((capability) => (
                  <Badge
                    key={capability}
                    variant="outline"
                    className="border-[hsl(var(--riel-texto)/0.35)] text-[hsl(var(--riel-texto))]"
                  >
                    {CAPABILITY_LABELS[capability]}
                  </Badge>
                ))
              )}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-guia="menu-usuario"
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 rounded-md border border-transparent px-1.5 py-1 text-left",
                  "transition-colors hover:border-[hsl(var(--riel-texto)/0.25)] hover:bg-[hsl(var(--riel-texto)/0.10)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
              >
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[hsl(var(--riel-texto)/0.16)] text-2xs font-medium text-[hsl(var(--riel-texto))]">
                  {initials(profile?.displayName)}
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate text-2xs font-medium">
                    {profile?.displayName ?? "Sesión"}
                  </span>
                  <span className="riel-texto-apagado block truncate text-2xs">
                    {activeMembership ? ROLE_LABELS[activeMembership.role] : "Sin rol"}
                  </span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-60">
              <DropdownMenuLabel>{profile?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled className="text-2xs">
                <UserRound />
                {memberships.length}{" "}{t("cliente(s) en tu cartera")}</DropdownMenuItem>
              {/*
                EL IDIOMA YA NO ESTÁ AQUÍ. Cesar no lo encontraba, y con razón:
                estaba a dos clics dentro del menú del avatar, al fondo del
                riel. Subió a un botón propio en la barra, junto al tema. Y el
                formato y la moneda tampoco: ya están en la barra de arriba,
                junto a la sociedad, y repetirlos aquí como fila apagada era un
                dato dos veces y una acción ninguna.
              */}
              {/* LA PUERTA PARA VOLVER A VER LA GUÍA, que se prometía y no estaba. */}
              <DropdownMenuItem onSelect={() => setGuiaAbierta(true)}>
                <Compass />
                {t("Ver la guía de la plataforma")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => void handleSignOut()}>
                <LogOut />
                {t("Cerrar sesión")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* ---------------- Area de trabajo ---------------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-chrome shrink-0 items-center gap-2 border-b bg-surface px-3">
          {/*
            LAS DOS COORDENADAS DEL ALCANCE, Y CADA UNA CON SU NOMBRE ENCIMA.

            Cesar: «el cliente es Siete y dentro de Siete tiene cada sociedad;
            debería ser más claro, porque sale sólo "Siete PE" y un botón de
            lista, y a la derecha sale "Siete Agency"».

            Tenía razón, y el problema no era el tamaño: **las dos coordenadas se
            veían iguales**. Dos desplegables del mismo ancho, con la misma
            tipografía, separados por una barra — nada decía cuál era el cliente
            y cuál la sociedad, ni que la segunda está DENTRO de la primera. Y
            «Siete PE» se lee como un solo nombre cuando en realidad son el
            cliente y el país de su sociedad por defecto.

            Ahora cada uno lleva su rótulo en versalitas —CLIENTE y SOCIEDAD, los
            dos roles de la lámina 18— y entre ellos va un «›» y no una barra: el
            chevron dice contención, la barra dice alternativa. Es el mismo signo
            que usa el producto en «Contabilidad › Maestros › Sociedades».
          */}
          <div data-guia="selector-cliente" className="flex min-w-0 shrink-0 items-center gap-1">
            <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="eyebrow hidden shrink-0 text-muted-foreground lg:inline">
              {t("cliente")}
            </span>
          </div>
          <Select value={activeTenantId ?? undefined} onValueChange={setActiveTenantId}>
            <SelectTrigger
              className="h-7 w-[13rem] border-transparent bg-transparent font-medium shadow-none hover:border-input hover:bg-surface-sunken"
              aria-label={t("Cliente activo")}
            >
              <SelectValue placeholder={t("Selecciona un cliente")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t("Clientes en tu cartera")}</SelectLabel>
                {/*
                  SIN PAIS JUNTO AL CLIENTE, y es una correccion de Cesar:
                  «elimina el PE al lado de Siete (cliente); cada sociedad si
                  esta bien saber que pais es».

                  Tiene razon y el motivo es que el pais del CLIENTE no
                  significa nada: Siete opera en PE, MX, BR y PA. El «PE» que
                  salia era el pais del tenant —un dato administrativo, el del
                  contrato— y al pegarse al nombre se leia como si el cliente
                  fuera peruano. La coordenada que si tiene pais es la SOCIEDAD,
                  y ahi sigue estando (`EntitySelect`).
                */}
                {memberships.map((membership) => (
                  <SelectItem key={membership.tenantId} value={membership.tenantId}>
                    <span className="truncate">{membership.tenantName}</span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          {/* «›» y no «/»: el chevron dice CONTENCIÓN, la barra dice alternativa. */}
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-border-strong" aria-hidden="true" />

          <EntitySelect />

          {/* El idioma y el formato siguen al pais de la ENTIDAD, no al navegador. */}
          <span
            className="hidden items-center gap-1.5 rounded-sm border bg-surface-sunken px-1.5 py-px md:inline-flex"
            title={`El idioma, el formato numerico y la terminologia fiscal siguen al pais de la entidad (${i18n.pais}), no a tu navegador. Aqui el impuesto a las ventas se llama ${i18n.term.impuesto_ventas} y el identificador fiscal ${i18n.term.identificador_fiscal}.`}
          >
            <Globe className="h-3 w-3 text-muted-foreground" />
            <span className="code text-2xs text-muted-foreground">{i18n.locale}</span>
            <span className="text-2xs text-border-strong">·</span>
            <span className="code text-2xs font-medium text-foreground">
              {i18n.term.impuesto_ventas}
            </span>
            <span className="text-2xs text-border-strong">·</span>
            <span className="code text-2xs font-medium text-foreground">
              {i18n.term.identificador_fiscal}
            </span>
          </span>

          {USE_MOCKS ? (
            <Badge
              variant="warning-soft"
              title={t("VITE_USE_MOCKS=true: datos de ejemplo, sin backend")}
            >
              {t("datos mock")}</Badge>
          ) : null}

          {/*
            A LA DERECHA, LO DE QUIEN MIRA. Idioma y tema: ninguno de los dos es
            una coordenada del dato —eso son cliente y sociedad, a la izquierda—
            y ponerlos juntos y aparte es lo que hace la diferencia legible sin
            explicarla.
          */}
          <div data-guia="idioma-tema" className="ml-auto flex items-center gap-0.5">
            <BotonDeIdioma />
            <ThemeToggle />
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-[120rem] space-y-3 p-4">
            {/*
              LA NOTA DE MARTY, en el mismo sitio en TODAS las pantallas.

              Se monta una sola vez y no en cada layout: el ambito lo deduce de
              la ruta (`lib/marty.ts`), asi que las veinticuatro secciones —y la
              que alguien escriba el mes que viene— hablan sin tocar su archivo.
              Que aparezca siempre en el mismo lugar tambien es la decision: se
              aprende donde habla Marty, en vez de buscarlo.

              Se silencia por seccion y se recuerda. Ver `MartyNota`.
            */}
            <MartyNota />
            {/*
              Segunda red, por pantalla. La de `main.tsx` evita la pantalla en
              blanco; esta ademas conserva la navegacion: si revienta el diario,
              la barra lateral sigue ahi y se puede ir a otra parte sin recargar.
              La `key` es la ruta a proposito — al navegar, el limite se reinicia
              solo y no arrastra el error de la pantalla anterior.
            */}
            <ErrorBoundary key={pathname} ambito={pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      {/*
        LOS DOS PANELES FLOTANTES, montados en el layout y no en cada pantalla.

        La GUÍA (punto 4) recorre la plataforma y por eso tiene que sobrevivir a
        la navegación: montada dentro de una pantalla, se desmontaría en la
        primera parada. MARTY (punto 12) contesta sobre lo que tienes delante, y
        para eso necesita estar en todas.

        Esquinas distintas a propósito: Marty a la izquierda —donde lo pidió
        Cesar— y la guía a la derecha. En la misma se taparían.
      */}
      <GuiaDeLaPlataforma
        abierta={guiaAbierta}
        tenantId={activeTenantId}
        onCerrar={() => setGuiaAbierta(false)}
      />
      <MartyFlotante />
    </div>
  )
}

function initials(name: string | null | undefined) {
  if (!name) return "··"
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("")
}
